"use client";
import React, { CSSProperties, useState, useEffect, useCallback } from "react";
import useEmblaCarousel from "embla-carousel-react";
import { cn } from "@pulsex/utils";
import "@/styles/carousel-base.css";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faAngleLeft, faAngleRight } from "@fortawesome/free-solid-svg-icons";
import ImageUI from "@/components/CustomImage/ImageUI";
import { Post } from "@pulsex/types";

export default function MediaGalleryCarousel({ post }: { post: Post }) {
    const [emblaRef, emblaApi] = useEmblaCarousel();
    // Add state to track prev/next capabilities
    const [canScrollPrev, setCanScrollPrev] = useState(false);
    const [canScrollNext, setCanScrollNext] = useState(false);

    // Update scroll state when carousel changes
    const onScroll = useCallback(() => {
        if (!emblaApi) return;
        setCanScrollPrev(emblaApi.canScrollPrev());
        setCanScrollNext(emblaApi.canScrollNext());
    }, [emblaApi]);

    // Initialize scroll state and add listeners
    useEffect(() => {
        if (!emblaApi) return;

        // Set initial state
        setCanScrollPrev(emblaApi.canScrollPrev());
        setCanScrollNext(emblaApi.canScrollNext());

        // Add listeners for carousel changes
        emblaApi.on('select', onScroll);
        emblaApi.on('reInit', onScroll);

        // Cleanup
        return () => {
            emblaApi.off('select', onScroll);
            emblaApi.off('reInit', onScroll);
        };
    }, [emblaApi, onScroll]);

    if (!post) return null;

    const images = [{ url: post.image }, ...post.filterMedias((m) => !!m)];

    return (
        <section className={cn("container px-80 mx-auto my-5")}>
            <div
                className="base-carousel full-carousel embla relative max-w-2xl mx-auto lg:p-5"
                style={
                    {
                        "--slide-size": "100%",
                    } as CSSProperties
                }
            >
                <div className="embla__viewport p-1" ref={emblaRef}>
                    <div className="embla__container">
                        {images.map((slide, index) => {
                            return (
                                <div className="embla__slide " key={index}>
                                    <ImageUI
                                        data={{ image: slide.url }}
                                        cropSize="standard"
                                        resolution={4}
                                    />
                                </div>
                            );
                        })}
                    </div>
                </div>

                {/* sides nav arrows */}
                <>
                    <FontAwesomeIcon
                        icon={faAngleLeft}
                        className={cn(
                            "bg-gray-100 dark:bg-bgblue dark:text-white p-2 rounded absolute left-0 top-1/2 -translate-x-1/2 -translate-y-1/2 w-4 mr-2 transition-all duration-300 hover:bg-blue hover:text-white cursor-pointer",
                            !canScrollPrev &&
                                "opacity-50 cursor-not-allowed",
                            "lg:left-5"
                        )}
                        onClick={() =>
                            canScrollPrev && emblaApi?.scrollPrev()
                        }
                    />
                    <FontAwesomeIcon
                        icon={faAngleRight}
                        className={cn(
                            "bg-gray-100 dark:bg-bgblue dark:text-white p-2 rounded absolute right-0 top-1/2 translate-x-1/2 -translate-y-1/2 w-4 ml-2 transition-all duration-300 hover:bg-blue hover:text-white cursor-pointer",
                            !canScrollNext &&
                                "opacity-50 cursor-not-allowed text-cyan-300",
                            "lg:right-5"
                        )}
                        onClick={() =>
                            canScrollNext && emblaApi?.scrollNext()
                        }
                    />
                </>
            </div>
        </section>
    );
}
